// ────────────────────────────────────────────────────────────────────────────
// Which Work View tabs this organisation is offered.
//
// Stored on the Directory `Organisation` as `tmsWorkViewTabsJson` — a JSON
// object keyed by tab name — and edited by Stratum support from the Work View
// Tabs card on Central Configuration.
//
// ONE RULE, EVERYWHERE: A MISSING KEY MEANS ON. Null column, unparseable
// string, a tab this build has but the saved map has never heard of — all
// resolve to enabled. That is what lets a sixth tab ship without arriving
// switched off for every organisation that had once pressed Save, and it makes
// the un-configured state (every org today) mean "everything", so nothing
// changes for anybody until support deliberately turns something off.
//
// The BE agrees on the shape but not the names: it validates an object of
// booleans with at least one `true` and stops there, because the tab set is a
// product decision and a BE that enumerated it would ship in lock-step with
// every new tab.
// ────────────────────────────────────────────────────────────────────────────
import { WORK_VIEW_PAGES, type WorkViewPage } from '@/stores/workView';

/** Every tab, on. The shipped default and the fallback for unreadable input. */
export type WorkViewTabMap = Record<WorkViewPage, boolean>;

export function allTabsEnabled(): WorkViewTabMap {
  return Object.fromEntries(WORK_VIEW_PAGES.map((page) => [page, true])) as WorkViewTabMap;
}

/**
 * Read the stored map into a complete, every-tab-present record.
 *
 * <p>
 * Never throws and never returns a partial map. A malformed string is not
 * worth a broken screen — the operator would see no tabs at all and have no
 * way to tell that from "support switched them off" — so anything unreadable
 * falls back to every tab, which is also what an un-configured org gets.
 * </p>
 */
export function parseWorkViewTabs(json: string | null | undefined): WorkViewTabMap {
  const fallback = allTabsEnabled();
  if (!json) return fallback;

  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return fallback;
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return fallback;

  // `fallback` is a fresh every-tab-on record, so narrowing it in place IS
  // the "missing key means on" rule rather than a shortcut around it.
  const stored = raw as Record<string, unknown>;
  const map = fallback;
  for (const page of WORK_VIEW_PAGES) {
    // Only an explicit `false` takes a tab away. A non-boolean value is
    // treated as absent rather than as "off", on the same reasoning as above.
    if (stored[page] === false) map[page] = false;
  }
  return map;
}

/** The tabs to render, in the product's own order. */
export function enabledWorkViewPages(json: string | null | undefined): WorkViewPage[] {
  const map = parseWorkViewTabs(json);
  return WORK_VIEW_PAGES.filter((page) => map[page]);
}

/**
 * The wire value for a map. Always writes EVERY tab this build knows about,
 * never just the disabled ones: the BE's "at least one enabled" check counts
 * `true` values in what it is sent, and a sparse map could not tell a tab that
 * is off from one that did not exist when the row was written.
 */
export function serialiseWorkViewTabs(map: WorkViewTabMap): string {
  return JSON.stringify(
    Object.fromEntries(WORK_VIEW_PAGES.map((page) => [page, map[page] !== false])),
  );
}
